import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LogIn, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AnimatedBackground from '../components/AnimatedBackground';
import BackgroundMusic from '../components/BackgroundMusic';
import MathCaptcha from '../components/MathCaptcha';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: ''
  });
  const [captchaData, setCaptchaData] = useState({
    captchaId: '',
    captchaAnswer: ''
  });
  const [captchaError, setCaptchaError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCaptchaChange = (captchaId, captchaAnswer) => {
    setCaptchaData({ captchaId, captchaAnswer });
    setCaptchaError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CAPTCHA
    if (!captchaData.captchaAnswer) {
      setCaptchaError('Please solve the math problem');
      return;
    }

    setIsSubmitting(true);

    const result = await login(
      formData.usernameOrEmail, 
      formData.password,
      captchaData.captchaId,
      parseInt(captchaData.captchaAnswer)
    );

    if (result.success) {
      toast({
        title: 'Login Successful',
        description: 'Welcome back!',
      });
      navigate('/dashboard');
    } else {
      toast({
        title: 'Login Failed',
        description: result.error,
        variant: 'destructive',
      });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 flex items-center justify-center p-4">
      <BackgroundMusic audioUrl="/audio/background-music.mp3" />
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-white hover:text-white/80 mb-6"
        >
          <ArrowLeft className="mr-2" />
          Back to Home
        </Button>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0d6571] rounded-full mb-4">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome Back</h1>
            <p className="text-gray-600 mt-2">Login to access your investor dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Username or Email
              </label>
              <Input
                type="text"
                name="usernameOrEmail"
                value={formData.usernameOrEmail}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                placeholder="Enter your username or email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex items-center justify-end">
              <Link 
                to="/forgot-password" 
                className="text-sm text-[#0d6571] hover:text-[#d97841] font-medium"
              >
                Forgot password?
              </Link>
            </div>

            {/* CAPTCHA Component */}
            <MathCaptcha 
              onCaptchaChange={handleCaptchaChange}
              error={captchaError}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
            >
              {isSubmitting ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#0d6571] hover:text-[#d97841] font-semibold">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
